import { getKernelApiBase, getOrgContext } from './cloudApi';

const HEALTH_VALUES = new Set(['healthy', 'degraded', 'unavailable', 'unknown']);
let healthRefreshInFlight = null;

export function canonicalControlChannel(channel) {
    if (channel === 'web' || channel === 'browser') return 'email';
    if (channel === 'google_chat') return 'googlechat';
    if (channel === 'microsoft_teams') return 'msteams';
    return channel || '';
}

export function normalizeChannelControl(raw = {}) {
    const channel = canonicalControlChannel(raw.channel || raw.channel_id || raw.channel_type);
    const enablementState = String(raw.enablement_state || '').toLowerCase();
    const enabled = typeof raw.enabled === 'boolean'
        ? raw.enabled
        : enablementState === 'enabled'
            ? true
            : enablementState === 'disabled'
                ? false
                : null;
    const health = String(raw.runtime_health || raw.health || 'unknown').toLowerCase();
    return {
        ...raw,
        channel,
        enabled,
        dcm_defined: typeof raw.dcm_defined === 'boolean' ? raw.dcm_defined : null,
        runtime_health: HEALTH_VALUES.has(health) ? health : 'unknown',
    };
}

export function controlFor(controls, channel) {
    const wanted = canonicalControlChannel(channel);
    return (Array.isArray(controls) ? controls : []).find((item) => item.channel === wanted) || {
        channel: wanted,
        enabled: null,
        runtime_health: 'unknown',
    };
}

export function productionChannelDisabledReason(controls, channel) {
    const control = controlFor(controls, channel);
    if (control.enabled === false) return `${control.channel || 'This channel'} is disabled for this organization.`;
    if (control.enabled !== true) return 'Channel enablement is unavailable, so production sending is blocked.';
    return '';
}

export async function getChannelControlStatus() {
    const base = getKernelApiBase().replace(/\/$/, '');
    const { orgId, userId } = getOrgContext();
    const headers = {};
    if (orgId) headers['X-Org-ID'] = orgId;
    if (userId) headers['X-User-ID'] = userId;
    const response = await fetch(`${base}/channels/control-status`, {
        credentials: 'include',
        headers,
    });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Channel status failed (${response.status}): ${text || response.statusText}`);
    }
    const body = await response.json();
    const data = body?.data ?? body;
    const rows = Array.isArray(data?.channels) ? data.channels : [];
    return rows.map(normalizeChannelControl).filter((row) => row.channel);
}

export function refreshChannelHealth() {
    if (healthRefreshInFlight) return healthRefreshInFlight;
    const base = getKernelApiBase().replace(/\/$/, '');
    healthRefreshInFlight = fetch(`${base}/channels/runtime-health/refresh`, {
        method: 'POST', credentials: 'include',
    }).then(async (response) => {
        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Channel health test failed (${response.status}): ${text || response.statusText}`);
        }
        const body = await response.json();
        return body?.data ?? body;
    }).finally(() => { healthRefreshInFlight = null; });
    return healthRefreshInFlight;
}
